import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { 
  CheckSquare, ArrowLeft, Plus, Calendar as CalendarIcon,
  Trash2, Edit2, Circle, CheckCircle2, Clock
} from "lucide-react";
import { format, isToday, parseISO, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay } from "date-fns";


import TopNav from "@/components/TopNav";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TasksHub() {
  const [tasks, setTasks] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filter, setFilter] = useState("all");

  const [newTask, setNewTask] = useState({
    title: "", description: "", due_date: "", priority: "medium", status: "todo"
  });

  const getHeaders = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const res = await axios.get(`${API}/tasks`, {
        headers: getHeaders(),
        withCredentials: true
      });
      setTasks(res.data);
    } catch (err) {
      toast.error("Failed to load tasks");
    }
  };

  const createTask = async () => {
    try {
      await axios.post(`${API}/tasks`, newTask, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      toast.success("Task created");
      setIsAddOpen(false);
      setNewTask({ title: "", description: "", due_date: "", priority: "medium", status: "todo" });
      fetchTasks();
    } catch (err) {
      toast.error("Failed to create task");
    }
  };

  const updateTask = async (taskId, updates) => {
    try {
      await axios.put(`${API}/tasks/${taskId}`, updates, {
        headers: { ...getHeaders(), "Content-Type": "application/json" },
        withCredentials: true
      });
      fetchTasks();
    } catch (err) {
      toast.error("Failed to update task");
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`, {
        headers: getHeaders(),
        withCredentials: true
      });
      toast.success("Task deleted");
      fetchTasks();
    } catch (err) {
      toast.error("Failed to delete task");
    }
  };

  const toggleTaskStatus = (task) => {
    const newStatus = task.status === "done" ? "todo" : "done";
    updateTask(task.id, { status: newStatus });
  };

  const getTasksForDate = (date) => {
    return tasks.filter(t => t.due_date && isSameDay(parseISO(t.due_date), date));
  };

  const filteredTasks = tasks.filter(task => {
    if (filter === "all") return true;
    return task.status === filter;
  });

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "high": return "text-red-500 border-red-500";
      case "medium": return "text-amber-500 border-amber-500";
      default: return "text-slate-8000 border-slate-500";
    }
  };

  const getPriorityBadge = (priority) => {
    switch (priority) {
      case "high": return "badge-red";
      case "medium": return "badge-amber";
      default: return "badge-blue";
    }
  };

  // Calendar days with tasks
  const monthStart = startOfMonth(selectedDate);
  const monthEnd = endOfMonth(selectedDate);
  const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });

  return (
    <div className="min-h-screen bg-app">
      <TopNav />
      {/* Header */}
      <header className="border-b border-slate-200 bg-app/80 backdrop-blur-lg sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/dashboard" className="text-slate-600 hover:text-slate-700">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-600 flex items-center justify-center">
                <CheckSquare className="w-5 h-5 text-white" />
              </div>
              <span className="font-['Barlow_Condensed'] text-xl font-semibold tracking-tight">
                TASKS HUB
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="bg-white border-slate-300 rounded-none w-32" data-testid="filter-status-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-300 rounded-none">
                <SelectItem value="all">All Tasks</SelectItem>
                <SelectItem value="todo">To Do</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="done">Done</SelectItem>
              </SelectContent>
            </Select>

            <Dialog open={isAddOpen} onOpenChange={setIsAddOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary rounded-none" data-testid="add-task-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Task
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-slate-200 rounded-none max-w-md">
                <DialogHeader>
                  <DialogTitle className="font-['Barlow_Condensed']">Create New Task</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <Input
                    placeholder="Task title"
                    value={newTask.title}
                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    className="bg-app border-slate-300 rounded-none"
                    data-testid="task-title-input"
                  />
                  <Textarea
                    placeholder="Description (optional)"
                    value={newTask.description}
                    onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
                    className="bg-app border-slate-300 rounded-none"
                    data-testid="task-description-input"
                  />
                  <div>
                    <Label className="text-slate-600 text-sm">Due Date</Label>
                    <Input
                      type="date"
                      value={newTask.due_date}
                      onChange={(e) => setNewTask({ ...newTask, due_date: e.target.value })}
                      className="bg-app border-slate-300 rounded-none"
                      data-testid="task-duedate-input"
                    />
                  </div>
                  <Select value={newTask.priority} onValueChange={(v) => setNewTask({ ...newTask, priority: v })}>
                    <SelectTrigger className="bg-app border-slate-300 rounded-none" data-testid="task-priority-select">
                      <SelectValue placeholder="Priority" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-slate-300 rounded-none">
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button className="btn-primary rounded-none w-full" onClick={createTask} data-testid="save-task-btn">
                    Create Task
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Calendar */}
          <div className="bg-white border border-slate-200 p-6">
            <h2 className="font-['Barlow_Condensed'] text-xl font-semibold text-slate-800 mb-4">
              CALENDAR
            </h2>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={(date) => date && setSelectedDate(date)}
              className="rounded-none border-0"
              modifiers={{
                hasTask: (date) => getTasksForDate(date).length > 0
              }}
              modifiersStyles={{
                hasTask: { fontWeight: "bold", color: "#A78BFA" }
              }}
              data-testid="task-calendar"
            />
            
            {/* Tasks for selected date */}
            <div className="mt-4 pt-4 border-t border-slate-200">
              <h3 className="text-sm text-slate-600 mb-2">
                {format(selectedDate, "MMMM d, yyyy")}
              </h3>
              {getTasksForDate(selectedDate).length > 0 ? (
                <div className="space-y-2">
                  {getTasksForDate(selectedDate).map(task => (
                    <div 
                      key={task.id}
                      className="flex items-center gap-2 text-sm"
                    >
                      <div className={`w-2 h-2 rounded-full ${
                        task.priority === 'high' ? 'bg-red-500' :
                        task.priority === 'medium' ? 'bg-amber-500' : 'bg-app0'
                      }`} />
                      <span className={task.status === 'done' ? 'line-through text-slate-8000' : 'text-slate-700'}>
                        {task.title}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-8000 text-sm">No tasks for this day</p>
              )}
            </div>
          </div>

          {/* Task List */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="font-['Barlow_Condensed'] text-xl font-semibold text-slate-800">
                TASKS ({filteredTasks.length})
              </h2>
              <div className="flex gap-2 text-sm">
                <span className="text-emerald-500">{tasks.filter(t => t.status === 'done').length} done</span>
                <span className="text-slate-8000">•</span>
                <span className="text-amber-500">{tasks.filter(t => t.status === 'todo').length} pending</span>
              </div>
            </div>

            <div className="space-y-2">
              {filteredTasks.map((task) => (
                <div 
                  key={task.id}
                  className={`task-item flex items-start gap-4 ${task.status === 'done' ? 'opacity-60' : ''}`}
                  data-testid={`task-item-${task.id}`}
                >
                  <button
                    onClick={() => toggleTaskStatus(task)}
                    className="mt-1"
                    data-testid={`toggle-task-${task.id}`}
                  >
                    {task.status === 'done' ? (
                      <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <Circle className={`w-5 h-5 ${getPriorityColor(task.priority)}`} />
                    )}
                  </button>
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className={`font-semibold ${task.status === 'done' ? 'line-through text-slate-8000' : 'text-slate-200'}`}>
                          {task.title}
                        </h3>
                        {task.description && (
                          <p className="text-sm text-slate-8000 mt-1">{task.description}</p>
                        )}
                      </div>
                      <button
                        onClick={() => deleteTask(task.id)}
                        className="text-slate-8000 hover:text-red-400"
                        data-testid={`delete-task-${task.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    
                    <div className="flex items-center gap-3 mt-2">
                      <span className={`badge ${getPriorityBadge(task.priority)}`}>
                        {task.priority}
                      </span>
                      {task.due_date && (
                        <div className="flex items-center gap-1 text-xs text-slate-8000">
                          <Clock className="w-3 h-3" />
                          <span className="font-['JetBrains_Mono']">{task.due_date}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {filteredTasks.length === 0 && (
              <div className="text-center py-12">
                <CheckSquare className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <p className="text-slate-8000">No tasks found. Create your first task!</p>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
